import { exactGradeKey, GRADE_CATEGORIES, gradeKeyFromText } from "../core/primitives/grades";

/** The eBay grade grid (EbayGradeLinks): the tab it opens on, its hint, and the grade a card marks as its own. */

/** The grid tab holding a grade, or undefined for RAW, no grade, or one the grid does not carry. */
export function gradeCategoryOf(key: string | null | undefined): string | undefined {
  return GRADE_CATEGORIES.find((c) => c.grades.some((g) => g.key === key))?.id;
}

/**
 * The key a grade as written names in the grade grid: any grade the grid
 * carries, as the review row reads it (`gradeKeyFromText`), else the pricing
 * rule's reading, which makes a blank grade raw unless the card is slabbed.
 * Not `exactGradeKey` alone: it names no BGS grade, so a BGS slab's own grade
 * would be marked nowhere.
 */
export function gridGradeKey(text: string, slabbed: boolean): string | null {
  return gradeKeyFromText(text) ?? exactGradeKey(text, slabbed);
}

/**
 * The tab the grade grid opens on: the one holding the grade the price was read
 * from, else the card's own grade; undefined for neither. The price's source
 * first, since it is the one figure the reviewer approves and the only place
 * on the row that says where it came from.
 */
export function gradeGridTab(
  pricedKey: string | null | undefined,
  ownKey: string | null | undefined,
): string | undefined {
  return gradeCategoryOf(pricedKey) ?? gradeCategoryOf(ownKey);
}

/** The line above the grade grid: what its marks mean. */
export function gradeGridHint(pricedKey: string | null | undefined, ownKey: string | null | undefined): string {
  const ownInGrid = gradeCategoryOf(ownKey) !== undefined;
  if (pricedKey && ownInGrid && ownKey !== pricedKey) {
    // RAW has no button to say "Price from here" on: its tab is tinted instead.
    return pricedKey === "raw"
      ? "Filled: this card's grade. Its price is read from RAW"
      : "Filled: this card's grade. “Price from here”: the grade its price is read from";
  }
  if (pricedKey) return "Highlighted grade is the one this card is priced from";
  if (ownInGrid) return "Filled is this card's grade. Tap any grade for its eBay comps";
  if (ownKey === "raw") return "This card is ungraded";
  return "Tap a grade to view comps on eBay";
}
